import { useState, FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TiArrowSortedDown, TiArrowSortedUp } from "react-icons/ti";

import ArrowDownIcon from "assets/icons/ArrowDownIcon";
import type { SortInputOption } from "types/SortInputOption";

interface SortInputProp {
  options: SortInputOption[];
  value: string;
  onChange: (value: string) => void;
}

const SortInput: FC<SortInputProp> = ({ options, value, onChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen]);

  return (
    <div className="w-full space-y-1 relative" ref={dropdownRef}>
      <div
        className={`
          w-full flex flex-row items-stretch
          bg-white rounded-lg border border-[#9B9B9B] cursor-pointer overflow-hidden
          transition-all duration-200 ease-in-out outline-none
        `}
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        <div className="flex min-w-0 flex-1 items-center px-3 py-2">
          <p className="text-gray-700 font-medium select-none">
            {t("_student._display_order")}
          </p>
        </div>
        <div className="dropdown-chevron-segment shrink-0">
          <span
            aria-hidden="true"
            className="dropdown-chevron-divider"
          />
          <div
            className={`transition-transform duration-200 ease-out ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <ArrowDownIcon width={24} height={24} color={"#4B5563"} />
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full right-0 left-auto z-50 mt-1 max-w-[90vw] origin-top-right animate-in fade-in-0 slide-in-from-top-1 zoom-in-95 duration-200 ease-out">
          <ul
            className={`
            w-full min-w-[240px] bg-white rounded-lg border border-[#9B9B9B]
            shadow-lg shadow-gray-200/50 p-2 max-h-40 overflow-auto
            animate-in fade-in-0 zoom-in-95 duration-200
          `}
          >
            {options.map((option, index) => (
              <li
                key={option.label}
                className={`
                  p-3 flex justify-between items-center rounded-md
                  transition-colors duration-150
                  hover:bg-gray-50 border-b border-gray-100 last:border-b-0
                  ${index === 0 ? "rounded-t-md" : ""}
                  ${index === options.length - 1 ? "rounded-b-md" : ""}
                `}
              >
                <p className="text-gray-700 font-medium flex-1 whitespace-pre">
                  {t(option.label)}
                </p>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    className={`
                      p-2 text-lg rounded-md transition-all duration-200
                      hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-200
                      ${
                        value === option.value.asc
                          ? "bg-blue-500 text-white shadow-md hover:bg-blue-600"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                      }
                    `}
                    onClick={() => onChange(option.value.asc)}
                    title="Sort Ascending"
                  >
                    <TiArrowSortedDown />
                  </button>
                  <button
                    className={`
                      p-2 text-lg rounded-md transition-all duration-200
                      hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-200
                      ${
                        value === option.value.desc
                          ? "bg-blue-500 text-white shadow-md hover:bg-blue-600"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                      }
                    `}
                    onClick={() => onChange(option.value.desc)}
                    title="Sort Descending"
                  >
                    <TiArrowSortedUp />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SortInput;
